import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Mail, Eye, AlertCircle, CheckCircle } from "lucide-react";

function formatTimestamp(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return ts.slice(0, 16);
  }
}

export default function ReadReceipts({ matterReference, compact = false }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!matterReference) return;
    fetchReceipts();
  }, [matterReference]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.get(`${API}/hub/read-receipts/${encodeURIComponent(matterReference)}`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setReceipts(resp.data?.receipts || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  if (!matterReference || receipts.length === 0) return null;

  return (
    <div
      className={
        compact
          ? "bg-navy border border-navy-border rounded p-2 text-xs"
          : "bg-navy border border-navy-border rounded-lg p-4"
      }
      data-testid="read-receipts"
    >
      <h3
        className={
          compact
            ? "text-[11px] font-bold text-white flex items-center gap-1 mb-1"
            : "text-sm font-bold text-white flex items-center gap-2 mb-3"
        }
      >
        <Mail className={compact ? "w-3 h-3 text-teal" : "w-4 h-4 text-teal"} /> Email Read Receipts
      </h3>
      <div className="space-y-2">
        {receipts.map((r) => (
          <div key={r.token} className="flex items-center justify-between bg-navy rounded px-3 py-2 border border-navy-border/50" data-testid={`receipt-${r.token}`}>
            <div className="flex items-center gap-2 min-w-0">
              {r.first_opened ? (
                <Eye className="w-3.5 h-3.5 text-teal flex-shrink-0" />
              ) : r.needs_followup ? (
                <AlertCircle className="w-3.5 h-3.5 text-status-amber flex-shrink-0" />
              ) : (
                <CheckCircle className="w-3.5 h-3.5 text-txt-muted flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-xs text-white truncate">{r.client_email}</p>
                <p className="text-[10px] text-txt-muted">{r.document_type?.replace(/_/g, " ")}</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              {r.first_opened ? (
                <span className="text-[10px] text-teal">Opened {formatTimestamp(r.first_opened)}</span>
              ) : r.needs_followup ? (
                <span className="text-[10px] text-status-amber">Not opened — follow up needed</span>
              ) : (
                <span className="text-[10px] text-txt-muted">Sent {formatTimestamp(r.created_at)}</span>
              )}
              {r.open_count > 1 && (
                <p className="text-[9px] text-txt-muted">{r.open_count} opens</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
